import type { EnglischerAuszug, EnglischerKnoten, EnglischerStandard, KatalogKnoten, MangelVorlage } from './typen';
import { hatPlatzhalter } from './vorlage';
import { vorschriftEnglisch } from './vorschriftEnglisch';

/**
 * Englische Fassung des Mängelkatalogs (28.09.): Lesen der erzeugten
 * Standardübersetzung (standard/englisch.generated.json) und englische
 * Vorlage eines Mangels. Rangfolge je Feld: englisches Feld des Overlays
 * bzw. eigenen Knotens (…En) → englischer Standard → deutscher Text als
 * Rückfall (z. B. eigene Einträge ohne Übersetzung).
 */

type Roh = Record<string, unknown>;

const istObjekt = (v: unknown): v is Roh => !!v && typeof v === 'object' && !Array.isArray(v);
const zeichen = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
/** Nicht leerer Text (nach trim) oder undefined. */
const gesetzt = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

function knotenLesen(roh: unknown): EnglischerKnoten | null {
    if (!istObjekt(roh)) return null;
    const kurz = gesetzt(roh.kurz);
    if (!kurz) return null;
    const k: EnglischerKnoten = { kurz };
    const name = gesetzt(roh.name);
    if (name) k.name = name;
    const titel = zeichen(roh.titel);
    if (titel !== undefined) k.titel = titel;
    const text = zeichen(roh.text);
    if (text !== undefined) k.text = text;
    return k;
}

function auszugLesen(roh: unknown): EnglischerAuszug | null {
    if (!istObjekt(roh)) return null;
    const titel = zeichen(roh.titel);
    const text = zeichen(roh.text);
    if (titel === undefined || text === undefined) return null;
    return { titel, text, vorschriftKurz: zeichen(roh.vorschriftKurz) ?? '' };
}

/**
 * Liest englisch.generated.json tolerant (nie werfen). Unbrauchbare Einträge
 * fallen einzeln weg; fehlt alles, ist die Fassung leer (Rückfall deutsch).
 */
export function englischNormalisieren(roh: unknown): EnglischerStandard {
    const aus: EnglischerStandard = { knoten: {}, auszug: {} };
    if (!istObjekt(roh)) return aus;
    if (istObjekt(roh.knoten)) {
        for (const [id, wert] of Object.entries(roh.knoten)) {
            const k = knotenLesen(wert);
            if (id && k) aus.knoten[id] = k;
        }
    }
    if (istObjekt(roh.auszug)) {
        for (const [id, wert] of Object.entries(roh.auszug)) {
            const a = auszugLesen(wert);
            if (id && a) aus.auszug[id] = a;
        }
    }
    return aus;
}

/** Englische Standardtexte eines Mangels: Beschriftung des Knotens und Auszug seines Eintrags. */
export interface EnglischeQuelle {
    knoten?: EnglischerKnoten;
    auszug?: EnglischerAuszug;
}

/**
 * Englische Vorlage eines Mangels aus der deutschen (`de`, schon mit allen
 * Feldern des Knotens aufgelöst) und dem Knoten `k` (Standard samt Overlay
 * bzw. eigener Knoten).
 * - titel/text: k.titelEn/textEn → Standardübersetzung des Knotens → des
 *   Eintrags → deutsch.
 * - vorschrift: eine eigene (deutsche) Vorschrift am Knoten → deren englische
 *   Kurzform; sonst die des englischen Auszugs; sonst vorschriftEnglisch(de).
 * - platzhalter: Lücke in der deutschen oder der englischen Fassung.
 */
export function englischeVorlage(de: MangelVorlage, k: KatalogKnoten, quelle: EnglischeQuelle = {}): MangelVorlage {
    const titel = gesetzt(k.titelEn) ?? gesetzt(quelle.knoten?.titel) ?? gesetzt(quelle.auszug?.titel) ?? de.titel;
    const text = gesetzt(k.textEn) ?? gesetzt(quelle.knoten?.text) ?? gesetzt(quelle.auszug?.text) ?? de.text;
    const vorschrift = gesetzt(k.vorschrift)
        ? vorschriftEnglisch(de.vorschrift)
        : gesetzt(quelle.auszug?.vorschriftKurz) ?? vorschriftEnglisch(de.vorschrift);
    const v: MangelVorlage = {
        titel,
        text,
        vorschrift,
        platzhalter: de.platzhalter || hatPlatzhalter(titel) || hatPlatzhalter(text),
    };
    if (de.eintragId !== undefined) v.eintragId = de.eintragId;
    if (de.dringlichkeit) v.dringlichkeit = de.dringlichkeit;
    if (typeof de.fristTage === 'number') v.fristTage = de.fristTage;
    return v;
}
